import type { Drawer } from '@mui/material';
import { ToggleButton } from '@mui/material';
import { CONTRACT_STATUSES, SYNC_STATUSES } from '@weft/types';
import type { ContractStatus, SyncStatus } from '@weft/types';
import { values, withDefault } from '@weft/utils';
import React from 'react';
import type { ComponentProps } from 'react';
import { getContractStatusLabel } from '../../../../components/design/ContractStatusChip';
import { FilterDrawer } from '../../../../components/design/filter-drawer/FilterDrawer';
import { usePageParam } from '../../../../hooks/search-params/use-page-param';
import { useContractStatusesParam } from './hooks/use-contract-statuses-param';
import { useContractSyncStatusesParam } from './hooks/use-contract-sync-statuses-params';

export const ContractsFilterDrawer: React.FC<ComponentProps<typeof Drawer>> = (props) => {
  const [selectedContractStatuses, setSelectedContractStatuses] = useContractStatusesParam();
  const [selectedContractSyncStatuses, setSelectedContractSyncStatuses] = useContractSyncStatusesParam();

  const [_page, setPage] = usePageParam();

  const handleContractStatusFiltersChange = (contractStatus: ContractStatus) => {
    if (selectedContractStatuses?.includes(contractStatus)) {
      setSelectedContractStatuses(selectedContractStatuses.filter((status) => status !== contractStatus));
    } else {
      const currentSelectedContractStatuses = withDefault(selectedContractStatuses, []);
      setSelectedContractStatuses([...currentSelectedContractStatuses, contractStatus]);
    }

    setPage(0);
  };

  const handleContractsSyncStatusFiltersChange = (syncStatus: SyncStatus) => {
    if (selectedContractSyncStatuses?.includes(syncStatus)) {
      const filtered = selectedContractSyncStatuses.filter((status) => status !== syncStatus);
      setSelectedContractSyncStatuses(filtered);
    } else {
      const currentSelectedContractStatuses = withDefault(selectedContractSyncStatuses, []);
      setSelectedContractSyncStatuses([...currentSelectedContractStatuses, syncStatus]);
      setPage(0);
    }
  };

  return (
    <FilterDrawer {...props}>
      <FilterDrawer.Section label="Contract Status">
        <div className="flex flex-wrap gap-2">
          {
            values(CONTRACT_STATUSES).map(contractStatus => (
              <ToggleButton
                key={crypto.randomUUID()}
                value={contractStatus}
                className="border-style text-style rounded px-4 py-2 text-base font-normal"
                sx={{
                  textTransform: 'none',
                }}
                selected={selectedContractStatuses?.includes(contractStatus)}
                onChange={() => handleContractStatusFiltersChange(contractStatus)}
              >
                {getContractStatusLabel(contractStatus)}
              </ToggleButton>
            ))
          }
        </div>
      </FilterDrawer.Section>
      <FilterDrawer.Section label="Contract Sync Status">
        <div className="flex flex-wrap gap-2">
          {
            values(SYNC_STATUSES).map(syncStatus => (
              <ToggleButton
                key={crypto.randomUUID()}
                value={syncStatus}
                className="border-style text-style rounded px-4 py-2 text-base font-normal"
                sx={{
                  textTransform: 'none',
                }}
                selected={selectedContractSyncStatuses?.includes(syncStatus)}
                onChange={() => handleContractsSyncStatusFiltersChange(syncStatus)}
              >
                {syncStatus}
              </ToggleButton>
            ))
          }
        </div>
      </FilterDrawer.Section>
    </FilterDrawer>
  );
};